import React, { useState } from 'react';
import PropTypes from 'prop-types';

import ChevronDown from '@decisiv/iconix/lib/components/ChevronDown';
import ChevronUp from '@decisiv/iconix/lib/components/ChevronUp';
import Popover from '@decisiv/ui-components/lib/components/Popover';
import Tag from '@decisiv/ui-components/lib/components/Tag';

import { OverflowPanel, OverflowTrigger } from './styles';

/**
 * The "expand for the rest" affordance used by the accounts and permissions
 * cells and by the Edit User header: a chevron trigger that flips up while its
 * panel is open.
 *
 * Visibility is controlled rather than left to Popover so the chevron can track
 * it. The panel is a Popover plus plain markup rather than the KeyDS Menu, which
 * crashes on the React 16 portal pins (see ManagementLayout/UserMenu.jsx).
 *
 * Two trigger shapes. `count` renders the design's single pill — the number and
 * the chevron inside one outline Tag, divided — which is a KeyDS Tag with an
 * `action`, so the shape and the divider come from the design system rather than
 * from CSS of ours. `summary` renders any tag of the caller's followed by a bare
 * chevron, for the cases where the trigger carries a label instead of a count.
 */
function OverflowPopover({ ariaLabel, children, count, summary }) {
  const [isOpen, setIsOpen] = useState(false);
  const toggle = () => setIsOpen((open) => !open);
  const Chevron = isOpen ? ChevronUp : ChevronDown;

  // KeyDS gives the Tag `tabIndex={0}` and an onClick but no key handling, so a
  // keyboard user could focus the pill and not open it. The plain <button> trigger
  // below needs none of this; this one has to say what it is as well.
  const keyboardActivation = (event) => {
    if (event.key !== 'Enter' && event.key !== ' ') {
      return;
    }

    event.preventDefault();
    toggle();
  };

  const target =
    count === undefined ? (
      <OverflowTrigger
        type="button"
        aria-label={ariaLabel}
        aria-expanded={isOpen}
        onClick={toggle}
      >
        {summary}
        <Chevron size="small" />
      </OverflowTrigger>
    ) : (
      <Tag
        action={toggle}
        actionAttributes={{
          'aria-label': ariaLabel,
          onKeyDown: keyboardActivation,
          role: 'button',
        }}
        actionIcon={Chevron}
        text={String(count)}
        variant="outline"
      />
    );

  return (
    <Popover
      hideOnClickOutside
      onRequestHide={() => setIsOpen(false)}
      placement="bottom-start"
      showArrow={false}
      visible={isOpen}
      target={target}
    >
      <OverflowPanel>{children}</OverflowPanel>
    </Popover>
  );
}

OverflowPopover.propTypes = {
  ariaLabel: PropTypes.string.isRequired,
  children: PropTypes.node.isRequired,
  /** How many entries are hidden. Renders the pill trigger; excludes `summary`. */
  count: PropTypes.number,
  /** Rendered inside the trigger, before the chevron. Excludes `count`. */
  summary: PropTypes.node,
};

OverflowPopover.defaultProps = {
  count: undefined,
  summary: undefined,
};

export default OverflowPopover;
